import React, { useEffect, useState } from 'react';
import BaseModal from '../BaseModal';
import { INPUT, LABEL } from '../ui';
import { todayInput } from '../dashboard/range';
import { PAYMENT_METHODS, formatMoney, formatMonthLong, parseDollars } from './format';

// Records money received against one invoice. The amount starts at the full
// balance, since that is what usually arrives; a smaller one leaves the invoice
// open for the rest.
const PaymentModal = ({ invoice, onConfirm, onCancel }) => {
  const [amount, setAmount] = useState('');
  const [method, setMethod] = useState('ach');
  const [receivedOn, setReceivedOn] = useState(todayInput());
  const [reference, setReference] = useState('');
  const [error, setError] = useState('');
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!invoice) return;
    setAmount((invoice.balance_cents / 100).toFixed(2));
    setMethod('ach');
    setReceivedOn(todayInput());
    setReference('');
    setError('');
    setBusy(false);
  }, [invoice]);

  if (!invoice) return null;

  const handleSubmit = async (e) => {
    e.preventDefault();
    const cents = parseDollars(amount);
    if (!cents) {
      setError('Enter an amount, like 1250.00.');
      return;
    }
    if (cents > invoice.balance_cents) {
      setError(`That's more than the ${formatMoney(invoice.balance_cents)} still owed.`);
      return;
    }
    setBusy(true);
    const message = await onConfirm({ amount_cents: cents, method, received_on: receivedOn, reference });
    if (message) {
      setError(message);
      setBusy(false);
    }
  };

  return (
    <BaseModal
      open
      titleId="payment-modal-title"
      title="Record a payment"
      message={`${invoice.number} · ${invoice.company_name} · ${formatMonthLong(invoice.period)} — ${formatMoney(
        invoice.balance_cents,
      )} still owed.`}
      onSubmit={handleSubmit}
      onCancel={onCancel}
      confirmLabel={busy ? 'Saving…' : 'Record payment'}
      selectOnOpen
    >
      <div className="space-y-4">
        <div>
          <label htmlFor="payment-amount" className={LABEL}>Amount received</label>
          <input
            id="payment-amount"
            inputMode="decimal"
            value={amount}
            onChange={(e) => setAmount(e.target.value)}
            className={INPUT}
            autoComplete="off"
          />
        </div>
        <div className="grid gap-4 sm:grid-cols-2">
          <div>
            <label htmlFor="payment-method" className={LABEL}>Paid by</label>
            <select id="payment-method" value={method} onChange={(e) => setMethod(e.target.value)} className={INPUT}>
              {PAYMENT_METHODS.map((m) => (
                <option key={m.id} value={m.id}>{m.label}</option>
              ))}
            </select>
          </div>
          <div>
            <label htmlFor="payment-date" className={LABEL}>Received on</label>
            <input
              id="payment-date"
              type="date"
              value={receivedOn}
              max={todayInput()}
              onChange={(e) => setReceivedOn(e.target.value)}
              className={`${INPUT} [color-scheme:dark]`}
              required
            />
          </div>
        </div>
        <div>
          <label htmlFor="payment-reference" className={LABEL}>Reference <span className="text-gray-500 font-normal">(optional)</span></label>
          <input
            id="payment-reference"
            value={reference}
            onChange={(e) => setReference(e.target.value)}
            placeholder="Check number, transfer ID…"
            maxLength={120}
            className={INPUT}
          />
        </div>
        {error && <p className="text-sm text-red-300" role="alert">{error}</p>}
      </div>
    </BaseModal>
  );
};

export default PaymentModal;
